import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { loginSuccess } from '../../store/slices/authSlice';
import {
  getStoredAccessToken,
  persistMeetingAuth,
} from '../../utils/meetingAuth';

const autoLoginRequestCache = new Map();

const InterviewAutoLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [status, setStatus] = useState(
    'Verifying your interview security link...'
  );
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let redirectTimer = null;

    const roomId = String(searchParams.get('roomId') || '').trim();
    const inviteToken = String(searchParams.get('invite') || '').trim();

    const openJoinRoom = () => {
      navigate(
        `/join-room?roomIdGet=${encodeURIComponent(roomId)}`,
        { replace: true }
      );
    };

    if (roomId && !inviteToken && getStoredAccessToken()) {
      openJoinRoom();
      return undefined;
    }

    if (!roomId || !inviteToken) {
      const message = 'Invalid or broken interview link.';
      setError(message);
      toast.error(message);
      redirectTimer = window.setTimeout(
        () => navigate('/login', { replace: true }),
        1500
      );

      return () => window.clearTimeout(redirectTimer);
    }

    const performAutoLogin = async () => {
      try {
        const requestKey = `${roomId}:${inviteToken}`;
        let request = autoLoginRequestCache.get(requestKey);

        if (!request) {
          request = api.post('/rooms/auto-login-interview', {
            roomId,
            inviteToken,
          });

          autoLoginRequestCache.set(requestKey, request);

          void request
            .finally(() => {
              window.setTimeout(
                () => autoLoginRequestCache.delete(requestKey),
                5000
              );
            })
            .catch(() => undefined);
        }

        const response = await request;

        if (!response.data?.success) {
          throw new Error('Interview authentication failed.');
        }

        const {
          token,
          user,
          sessionId,
          status: participantStatus = 'waiting',
          meetingType = 'interview',
          access = {},
          message,
        } = response.data;

        const authPayload = persistMeetingAuth({
          token,
          user,
          sessionId,
          status: participantStatus,
          meetingType,
          access,
        });

        dispatch(loginSuccess(authPayload));

        if (cancelled) return;

        setStatus(
          message ||
            (access.isHost
              ? 'Admin access granted. Opening the interview...'
              : 'Access verified. Waiting for admin approval...')
        );

        openJoinRoom();
      } catch (requestError) {
        if (cancelled) return;

        const message =
          requestError.response?.data?.error ||
          requestError.response?.data?.message ||
          requestError.message ||
          'Authentication link expired or is invalid.';

        console.error('Interview auto-login error:', requestError);
        setError(message);
        toast.error(message);

        redirectTimer = window.setTimeout(
          () => navigate('/login', { replace: true }),
          1800
        );
      }
    };

    void performAutoLogin();

    return () => {
      cancelled = true;
      if (redirectTimer) window.clearTimeout(redirectTimer);
    };
  }, [dispatch, navigate, searchParams]);

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center bg-gray-900 px-6 text-center text-white">
      {error ? (
        <>
          <div className="mb-4 text-4xl text-red-500">⚠️</div>
          <h2 className="text-xl font-medium text-red-400">{error}</h2>
          <p className="mt-2 text-sm text-gray-400">
            Opening the login page...
          </p>
        </>
      ) : (
        <>
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-b-4 border-t-4 border-indigo-500" />
          <h2 className="text-xl font-medium text-gray-300">{status}</h2>
          <p className="mt-2 text-sm text-gray-500">
            Setting up a secure interview session...
          </p>
        </>
      )}
    </div>
  );
};

export default InterviewAutoLogin;
